import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/observable/of';
import {
  HeadClothingMarkerOverride,
  HeadClothingPrivateMarker,
  HeadClothingPrivateMarkerNorm
} from '../../../../../models/clothing.model';
import { Group } from '../../../../../models/group.model';
import { GroupUser } from '../../../../../models/group-user.model';
import { ClothingService } from '../../../../../services/clothing.service';
import { ToastService } from '../../../../../services/toast.service';

@Component({
  selector: 'app-clothing-marker-overrides-modal',
  templateUrl: './clothing-marker-overrides-modal.component.html',
  styleUrls: ['./clothing-marker-overrides-modal.component.css']
})
export class ClothingMarkerOverridesModalComponent implements OnInit {
  @Input() public group: Group;

  public markers: HeadClothingPrivateMarker[] = [];
  public markerOverridesByUserId: { [userId: string]: HeadClothingMarkerOverride } = {};
  public selectedMarkerCode = '';
  public previewMarkerCode = '';
  public selectedUserIds: { [userId: string]: boolean } = {};
  public isLoading = false;
  public isSaving = false;

  constructor(
    public activeModal: NgbActiveModal,
    private clothingService: ClothingService,
    private toastService: ToastService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadInitialData();
  }

  getUsers(): GroupUser[] {
    if (!this.group || !this.group.activeUsers) {
      return [];
    }
    return this.group.activeUsers.slice().sort((firstUser, secondUser) => {
      return this.compareGroupUsers(firstUser, secondUser);
    });
  }

  trackByUserId(index: number, user: GroupUser): string {
    if (user && user.user && user.user.id) {
      return String(user.user.id);
    }
    return String(index);
  }

  trackByMarkerCode(index: number, marker: HeadClothingPrivateMarker): string {
    if (marker && marker.markerCode) {
      return marker.markerCode;
    }
    return String(index);
  }

  getUserName(user: GroupUser): string {
    if (!user || !user.user) {
      return '';
    }
    return [
      user.user.lastName,
      user.user.firstName,
      user.user.patronymicName
    ].filter((value) => !!value).join(' ');
  }

  getUserMarkerName(user: GroupUser): string {
    var override = this.getMarkerOverride(user);

    if (!override || !override.effectiveDepartmentMarkerCode) {
      return '';
    }
    var markerName = this.formatMarkerCode(override.effectiveDepartmentMarkerCode);

    if (this.isDefaultUserMarker(markerName)) {
      return '';
    }
    return markerName;
  }

  toggleUser(user: GroupUser): void {
    if (!user || !user.user || !user.user.id) {
      return;
    }
    var userId = String(user.user.id);
    this.selectedUserIds[userId] = !this.selectedUserIds[userId];
  }

  isUserSelected(user: GroupUser): boolean {
    if (!user || !user.user || !user.user.id) {
      return false;
    }
    return this.selectedUserIds[String(user.user.id)] === true;
  }

  selectMarker(marker: HeadClothingPrivateMarker): void {
    if (!marker || !marker.markerCode) {
      return;
    }
    this.selectedMarkerCode = marker.markerCode;
  }

  isMarkerSelected(marker: HeadClothingPrivateMarker): boolean {
    return !!marker && marker.markerCode === this.selectedMarkerCode;
  }

  showMarkerPreview(marker: HeadClothingPrivateMarker): void {
    if (!marker || !marker.markerCode) {
      return;
    }
    this.previewMarkerCode = marker.markerCode;
  }

  clearMarkerPreview(marker: HeadClothingPrivateMarker): void {
    if (!marker || this.previewMarkerCode !== marker.markerCode) {
      return;
    }
    this.previewMarkerCode = '';
  }

  getPreviewMarker(): HeadClothingPrivateMarker {
    return this.findMarkerByCode(this.previewMarkerCode) || this.findMarkerByCode(this.selectedMarkerCode);
  }

  getPreviewNorms(): HeadClothingPrivateMarkerNorm[] {
    return this.getMarkerNorms(this.getPreviewMarker());
  }

  getSelectedCount(): number {
    return this.getSelectedUserIds().length;
  }

  canAssign(): boolean {
    return !this.isSaving && !!this.selectedMarkerCode && this.getSelectedCount() > 0;
  }

  assignMarker(): void {
    if (!this.canAssign()) {
      return;
    }
    this.isSaving = true;
    this.clothingService.assignHeadMarkerOverride({
      markerCode: this.selectedMarkerCode,
      userIds: this.getSelectedUserIds()
    }).subscribe(
      () => this.handleAssignSuccess(),
      () => this.handleAssignError()
    );
  }

  canReset(): boolean {
    return !this.isSaving && this.getSelectedCount() > 0;
  }

  resetMarker(): void {
    if (!this.canReset()) {
      return;
    }
    var requests = this.getSelectedUserIds().map((userId) => {
      return this.clothingService.resetHeadMarkerOverride(userId);
    });

    this.isSaving = true;
    Observable.forkJoin(requests).subscribe(
      () => this.handleResetSuccess(),
      () => this.handleResetError()
    );
  }

  formatMarkerName(marker: HeadClothingPrivateMarker): string {
    if (!marker) {
      return '';
    }
    if (marker.displayName) {
      return marker.displayName;
    }
    return marker.markerCode;
  }

  getMarkerNorms(marker: HeadClothingPrivateMarker): HeadClothingPrivateMarkerNorm[] {
    if (!marker || !marker.norms) {
      return [];
    }
    return marker.norms;
  }

  close(): void {
    this.activeModal.dismiss();
  }

  private loadInitialData(): void {
    this.isLoading = true;
    Observable.forkJoin(
      this.clothingService.getHeadAssignableMarkers(),
      this.loadCurrentMarkerOverrides()
    ).subscribe(
      (response) => this.handleInitialDataLoaded(response),
      () => this.handleInitialDataError()
    );
  }

  private handleInitialDataLoaded(response: Object[]): void {
    this.markers = response[0] as HeadClothingPrivateMarker[] || [];
    this.markerOverridesByUserId = this.createMarkerOverrideLookup(
      response[1] as HeadClothingMarkerOverride[] || []
    );
    this.isLoading = false;
  }

  private handleInitialDataError(): void {
    this.markers = [];
    this.markerOverridesByUserId = {};
    this.isLoading = false;
    this.toastService.error(this.translate.instant('CLOTHING_MARKER_OVERRIDE_LOAD_ERROR'));
  }

  private handleAssignSuccess(): void {
    this.isSaving = false;
    this.toastService.success(this.translate.instant('CLOTHING_MARKER_OVERRIDE_SAVED'));
    this.activeModal.close('ok');
  }

  private handleAssignError(): void {
    this.isSaving = false;
    this.toastService.error(this.translate.instant('CLOTHING_MARKER_OVERRIDE_SAVE_ERROR'));
  }

  private handleResetSuccess(): void {
    this.isSaving = false;
    this.toastService.success(this.translate.instant('CLOTHING_MARKER_OVERRIDE_RESET'));
    this.activeModal.close('ok');
  }

  private handleResetError(): void {
    this.isSaving = false;
    this.toastService.error(this.translate.instant('CLOTHING_MARKER_OVERRIDE_RESET_ERROR'));
  }

  private getSelectedUserIds(): string[] {
    var result: string[] = [];
    for (var userId in this.selectedUserIds) {
      if (this.selectedUserIds.hasOwnProperty(userId) && this.selectedUserIds[userId] === true) {
        result.push(userId);
      }
    }
    return result;
  }

  private loadCurrentMarkerOverrides(): Observable<HeadClothingMarkerOverride[]> {
    var userIds = this.getGroupUserIds();

    if (!userIds.length) {
      return Observable.of([]);
    }
    return this.clothingService.getHeadMarkerOverrides(userIds);
  }

  private getGroupUserIds(): string[] {
    var userIds: string[] = [];

    this.getUsers().forEach((groupUser) => {
      if (!groupUser || !groupUser.user || !groupUser.user.id) {
        return;
      }
      this.appendUserId(userIds, String(groupUser.user.id));
    });
    return userIds;
  }

  private appendUserId(userIds: string[], userId: string): void {
    if (userIds.indexOf(userId) !== -1) {
      return;
    }
    userIds.push(userId);
  }

  private createMarkerOverrideLookup(
    overrides: HeadClothingMarkerOverride[]
  ): { [userId: string]: HeadClothingMarkerOverride } {
    var lookup: { [userId: string]: HeadClothingMarkerOverride } = {};

    if (!overrides || !overrides.length) {
      return lookup;
    }
    overrides.forEach((override) => {
      if (override && override.userId) {
        lookup[override.userId] = override;
      }
    });
    return lookup;
  }

  private getMarkerOverride(user: GroupUser): HeadClothingMarkerOverride {
    if (!user || !user.user || !user.user.id) {
      return null;
    }
    return this.markerOverridesByUserId[String(user.user.id)] || null;
  }

  private findMarkerByCode(markerCode: string): HeadClothingPrivateMarker {
    if (!markerCode || !this.markers || !this.markers.length) {
      return null;
    }
    for (var index = 0; index < this.markers.length; index++) {
      if (this.markers[index].markerCode === markerCode) {
        return this.markers[index];
      }
    }
    return null;
  }

  private formatMarkerCode(markerCode: string): string {
    var marker = this.findMarkerByCode(markerCode);

    if (marker) {
      return this.formatMarkerName(marker);
    }
    return markerCode;
  }

  private compareGroupUsers(firstUser: GroupUser, secondUser: GroupUser): number {
    var firstName = this.createSortableUserName(firstUser);
    var secondName = this.createSortableUserName(secondUser);

    if (firstName < secondName) {
      return -1;
    }
    if (firstName > secondName) {
      return 1;
    }
    return 0;
  }

  private createSortableUserName(groupUser: GroupUser): string {
    if (!groupUser || !groupUser.user) {
      return '';
    }
    return [
      groupUser.user.lastName,
      groupUser.user.firstName,
      groupUser.user.patronymicName
    ].filter((value) => !!value).join(' ').toLowerCase();
  }

  private isDefaultUserMarker(markerName: string): boolean {
    if (!markerName) {
      return true;
    }
    return markerName.toLowerCase() === 'user';
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/popups/clothing-marker-overrides/clothing-marker-overrides-modal.component.ts